import { useEffect, useRef, useState } from 'react';
import { buildURL } from 'react-imgix';
import cx from 'classnames';
import isNumber from 'lodash/isNumber';
import type SwiperInstance from 'swiper';
import { A11y, FreeMode, Keyboard, Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import cypressIds from 'Components/cypress_ids.json';
import type { MappedDocumentImage } from 'Components/pages/VehiclePurchaseSummary/sections/SellerDocumentsSection/SellerDocumentsSection.helpers';
import type { MappedVehicleImage } from 'Pages/vehicles/helpers/transformers';
import { applyCypressData } from 'Utilities/helpers';

import { useDebounceFailedImages } from '../Gallery.utils';
import { Image as ImageComponent } from '../Image';

import { IMAGE_SIZE, LAZY_PRELOAD_IMAGES, SPACE_BETWEEN_THUMBS, TABLET_SIZE } from './MainGallery.const';
import { Modal } from './Modal';
import { type SlideInfoEvents, SlideInfo } from './SlideInfo';
import { type TabEvents, Tabs } from './Tabs';
import { type ZoomEvents, Zoom } from './Zoom';

import styles from './MainGallery.module.scss';

export type GalleryImageWithTitle = MappedVehicleImage | MappedDocumentImage;

export interface GalleryEvents extends ZoomEvents, TabEvents, SlideInfoEvents {
	onClose?: (image: GalleryImageWithTitle) => void;
	onNext?: (image: GalleryImageWithTitle) => void;
	onPrev?: (image: GalleryImageWithTitle) => void;
	onSlideView?: (image: GalleryImageWithTitle) => void;
	onThumbClick?: (image: GalleryImageWithTitle) => void;
}

interface MainGalleryProps {
	active?: Nullable<number>;
	events?: GalleryEvents;
	hideTabs?: boolean;
	images: GalleryImageWithTitle[];
	overlayBadge?: React.ReactNode;
	shouldMaskImages?: boolean;
	showDownloadImageButtons?: boolean;
}

const swiperProps = {
	lazyPreloadPrevNext: LAZY_PRELOAD_IMAGES,
	observeParents: true,
	observer: true,
	watchSlidesProgress: true,
};

const RIGHT_ARROW_KEY = '39';
const LEFT_ARROW_KEY = '37';

export const MainGallery: React.FC<MainGalleryProps> = ({
	active = null,
	events,
	hideTabs,
	images,
	overlayBadge,
	shouldMaskImages,
	showDownloadImageButtons,
}) => {
	const lastActive = useRef(active);
	const [mainSwiper, setMainSwiper] = useState<SwiperInstance | null>(null);
	const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null);
	const [emptyCategory, setEmptyCategory] = useState('');
	const { failedImages, setFailedImage } = useDebounceFailedImages();

	const onPrevNext = (swiper: SwiperInstance) => {
		events?.onSlideView?.(images[swiper?.activeIndex]);
		swiper.swipeDirection = undefined;
		swiper.thumbs.swiper.clickedIndex = undefined;
	};

	const onNext = (swiper: SwiperInstance) => {
		events?.onNext?.(images[swiper?.activeIndex]);
		onPrevNext(swiper);
	};

	const onPrev = (swiper: SwiperInstance) => {
		events?.onPrev?.(images[swiper?.activeIndex]);
		onPrevNext(swiper);
	};

	const onSlideChange = (swiper: SwiperInstance) => {
		setEmptyCategory('');
		thumbsSwiper?.slideTo(swiper?.activeIndex);
		setTimeout(() => {
			lastActive.current = swiper.activeIndex;
			if (swiper.activeIndex !== swiper.thumbs.swiper.clickedIndex) {
				if (swiper.swipeDirection === 'next') onNext(swiper);
				if (swiper.swipeDirection === 'prev') onPrev(swiper);
			}
		});
	};

	const onResize = () => {
		mainSwiper?.pagination.update();
	};

	const onThumbClick = (swiper: SwiperInstance) => {
		setEmptyCategory('');
		if (images[swiper?.clickedIndex] && swiper.clickedIndex !== lastActive.current) {
			events?.onThumbClick?.(images[swiper?.clickedIndex]);
			events?.onSlideView?.(images[swiper?.clickedIndex]);
		}
	};

	const onKeyPress = (swiper: SwiperInstance, keyCode: string) => {
		if (`${keyCode}` === LEFT_ARROW_KEY && lastActive.current !== swiper.activeIndex) onPrev(swiper);
		if (`${keyCode}` === RIGHT_ARROW_KEY && lastActive.current !== swiper.activeIndex) onNext(swiper);
	};

	const onClose = () => {
		events?.onClose?.(images[mainSwiper?.activeIndex]);
	};

	useEffect(() => {
		if (isNumber(active)) {
			events?.onSlideView?.(images[active]);
			lastActive.current = active;
		}
	}, [active]); // eslint-disable-line

	useEffect(() => {
		// force swiper slides recalculation
		window?.dispatchEvent(new Event('resize'));

		if (mainSwiper) {
			mainSwiper.slideTo(active ?? 0, 0);
			setTimeout(() => {
				// delay pagination update
				mainSwiper.pagination?.update();
			});
		}
	}, [active, mainSwiper]);

	useEffect(() => {
		// precache big images
		images.forEach((it) => {
			const img = new Image();
			const isSigned = it.url.includes('s=');
			img.src = isSigned
				? it.url
				: buildURL(it.url, { auto: 'compress,format', w: IMAGE_SIZE.FULL }, { disableLibraryParam: true, src: it.url });
		});
	}, [images]);

	const shouldCenterSlides = (thumbsSwiper?.slides?.length ?? 0) > thumbsSwiper?.visibleSlides?.length;

	return (
		<Modal
			contentProps={{
				...applyCypressData(cypressIds.containers.vehicleDetailsPageGallery),
			}}
			isOpen={isNumber(active)}
			onClose={onClose}
		>
			{(isModalOpen: boolean) => (
				<>
					<Swiper
						keyboard
						navigation
						simulateTouch
						watchOverflow
						className={cx(styles.main, 'gallery-main')}
						modules={[A11y, Keyboard, Navigation, Pagination, Thumbs]}
						onKeyPress={onKeyPress}
						onNavigationNext={onNext}
						onNavigationPrev={onPrev}
						onResize={onResize}
						onSlideChange={onSlideChange}
						onSwiper={setMainSwiper}
						pagination={{
							dynamicBullets: true,
						}}
						thumbs={{ swiper: thumbsSwiper }}
						{...swiperProps}
					>
						{images.map(({ id, url }) => (
							<SwiperSlide key={id} zoom>
								<Zoom
									category={emptyCategory}
									disabled={!isModalOpen || failedImages[id] || !!emptyCategory}
									events={events}
								>
									<ImageComponent
										loading="lazy"
										onError={() => setFailedImage(id)}
										shouldMaskImage={Boolean(shouldMaskImages)}
										sizes={`(min-width: ${TABLET_SIZE}px) ${IMAGE_SIZE.FULL}vw, ${IMAGE_SIZE.DESKTOP}vw`}
										src={url}
										widths={[IMAGE_SIZE.DESKTOP, IMAGE_SIZE.FULL]}
									/>
								</Zoom>
							</SwiperSlide>
						))}
						<div className={styles.slotTop} slot="container-start">
							{!hideTabs && <Tabs category={emptyCategory} events={events} onEmptyTabClick={setEmptyCategory} />}
						</div>
						<div className={cx(styles.slotBottom, { [styles.hide]: !!emptyCategory })} slot="container-end">
							<SlideInfo events={events} showDownloadImageButtons={showDownloadImageButtons} />
						</div>
						{overlayBadge}
					</Swiper>
					<Swiper
						centerInsufficientSlides
						centeredSlidesBounds
						slideToClickedSlide
						centeredSlides={shouldCenterSlides}
						className={cx(styles.thumbs, 'gallery-thumbs')}
						modules={[FreeMode, Navigation, Thumbs]}
						onClick={onThumbClick}
						onSwiper={setThumbsSwiper}
						slidesPerView="auto"
						spaceBetween={SPACE_BETWEEN_THUMBS}
						{...swiperProps}
					>
						{images.map(({ category, id, presets }) => (
							<SwiperSlide key={id} data-category={category}>
								<ImageComponent
									loading="lazy"
									shouldMaskImage={Boolean(shouldMaskImages)}
									sizes={`${IMAGE_SIZE.THUMB}px`}
									src={presets.thumbnail}
									widths={[IMAGE_SIZE.THUMB]}
								/>
							</SwiperSlide>
						))}
					</Swiper>
				</>
			)}
		</Modal>
	);
};
